import { hasResource } from '@ctrl/db';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import type { ColumnVisibilityState } from '@tanstack/table-core';
import { Columns3, Download, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  DataTable,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { OrderQueue } from '@/components/orders/order-queue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';
import {
  DATE_RANGE_LABELS,
  FULFILLMENT_META,
  formatCalendarDay,
  formatCents,
  formatStoreDateTime,
  ORDER_STATUS_META,
  PAYMENT_STATUS_META,
} from '@/lib/order-display';
import {
  DATE_RANGES,
  defaultOrdersFilters,
  EXPORT_MAX_ROWS,
  exportOrdersCsv,
  FALLBACK_TIMEZONE,
  FULFILLMENT_TYPES,
  getStoreTimeZone,
  hasActiveOrdersFilters,
  listOpenOrders,
  listOrders,
  ORDER_STATUSES,
  type OrderListRow,
  type OrdersFilters,
  PAYMENT_STATUSES,
  subscribeToOrders,
} from '@/lib/orders';

/**
 * The order book, in two views.
 *
 * The **queue** is the floor's screen: every open order in lanes, with one
 * forward step per card, kept live by a Realtime subscription. The **table**
 * is the record: server-paged through `search_orders`, filtered in the
 * database, exportable.
 *
 * Products sync into a TanStack DB collection because the catalog is bounded
 * and instant in-memory filtering is the win. Orders cannot: the table grows
 * without bound and every row carries a shopper's name, email and address, so
 * searching, filtering and paging all stay in the database and one page of a
 * hundred crosses the wire. That is `manualPagination`, so this route owns the
 * page index rather than the table's own row model.
 */
export const Route = createFileRoute('/orders/')({
  component: OrdersPage,
});

const column = dataTableColumnHelper<OrderListRow>();

/**
 * `'all'` rather than an empty string: Radix's Select treats `''` as "no
 * value", which makes the trigger render its placeholder instead of the label.
 */
const ALL = 'all';

/** Which columns are shown, remembered per browser. */
const COLUMNS_STORAGE_KEY = 'ctrl:orders:columns';

function readColumnVisibility(): ColumnVisibilityState {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);

    return raw ? (JSON.parse(raw) as ColumnVisibilityState) : {};
  } catch {
    return {};
  }
}

function columns(timeZone: string) {
  return column.columns([
    column.accessor('orderNumber', {
      header: 'Order',
      enableHiding: false,
      cell: ({ row }) => (
        <Link
          to="/orders/$orderId"
          params={{ orderId: row.original.id }}
          className="font-medium tabular-nums hover:underline"
        >
          #{row.original.orderNumber}
        </Link>
      ),
    }),

    column.accessor('sortDate', {
      header: 'Date',
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap">{formatCalendarDay(getValue())}</span>
      ),
    }),

    column.accessor('placedAt', {
      header: 'Placed',
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap">{formatStoreDateTime(getValue(), timeZone)}</span>
      ),
    }),

    column.accessor('customerName', {
      header: 'Customer',
      cell: ({ row }) => (
        // `min-w-0` so a long address-book name truncates rather than widening
        // the column and pushing the money off-screen.
        <div className="min-w-0">
          <div className="truncate font-medium">{row.original.customerName}</div>
          <div className="text-muted-foreground truncate text-xs">
            {row.original.customerEmail ?? '—'}
          </div>
        </div>
      ),
    }),

    column.accessor('fulfillmentType', {
      header: 'Fulfillment',
      cell: ({ getValue }) => <span className="text-sm">{FULFILLMENT_META[getValue()].label}</span>,
    }),

    column.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => {
        const meta = ORDER_STATUS_META[getValue()];

        return <Badge className={meta.className}>{meta.label}</Badge>;
      },
    }),

    column.accessor('paymentStatus', {
      header: 'Payment',
      cell: ({ getValue }) => {
        const meta = PAYMENT_STATUS_META[getValue()];

        return <Badge className={meta.className}>{meta.label}</Badge>;
      },
    }),

    column.accessor('driverName', {
      header: 'Driver',
      cell: ({ getValue }) => <span className="text-sm">{getValue() ?? '—'}</span>,
    }),

    column.accessor('source', {
      header: 'Channel',
      cell: ({ getValue }) => <span className="text-sm capitalize">{getValue()}</span>,
    }),

    column.accessor('itemCount', {
      header: 'Items',
      meta: { className: 'text-right tabular-nums' },
      cell: ({ getValue }) => getValue().toLocaleString(),
    }),

    column.accessor('totalCents', {
      header: 'Total',
      enableHiding: false,
      meta: { className: 'text-right tabular-nums' },
      cell: ({ getValue }) => <span className="font-medium">{formatCents(getValue())}</span>,
    }),
  ]);
}

/** Columns off by default; the picker turns them on. */
const DEFAULT_HIDDEN: ColumnVisibilityState = { sortDate: false, driverName: false, source: false };

function OrdersPage() {
  const { activeAccountId, claims } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();

  const canWork = accountId ? hasResource(claims, accountId, 'orders') : false;

  const [view, setView] = useState<'queue' | 'all'>('queue');

  /*
   * The timezone decides what "today" means for the date presets and how a
   * timestamp is printed. It is a separate query so it is fetched once and then
   * held: paging must not re-read it, and it changes about never.
   */
  const timeZone = useQuery({
    queryKey: ['store-timezone', accountId],
    queryFn: () => getStoreTimeZone(accountId),
    enabled: Boolean(accountId),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const zone = timeZone.data ?? FALLBACK_TIMEZONE;

  /*
   * One subscription for both views. A change to any order of this account
   * drops the cached pages; the active query re-reads through `search_orders`
   * so RLS and the joins decide what appears.
   */
  useEffect(() => {
    if (!accountId) return;

    const channel = subscribeToOrders(accountId, () => {
      queryClient.invalidateQueries({ queryKey: ['orders', accountId] });
    });

    return () => {
      void channel.unsubscribe();
    };
  }, [accountId, queryClient]);

  const queue = useQuery({
    queryKey: ['orders', accountId, 'queue', zone],
    queryFn: () => listOpenOrders(accountId, zone),
    enabled: Boolean(accountId) && timeZone.isSuccess && view === 'queue',
    // A backstop for a dropped socket; the subscription is the main path.
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <Tabs value={view} onValueChange={(value) => setView(value as typeof view)}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Orders</h1>
            <p className="text-muted-foreground text-sm">
              {view === 'queue'
                ? queue.isLoading
                  ? 'Loading the queue…'
                  : `${(queue.data ?? []).length.toLocaleString()} open`
                : 'Every order, newest first.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <TabsList>
              <TabsTrigger value="queue">Queue</TabsTrigger>
              <TabsTrigger value="all">All orders</TabsTrigger>
            </TabsList>

            {canWork && (
              <Button asChild size="sm">
                <Link to="/orders/new">
                  <Plus className="size-4" />
                  New order
                </Link>
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="queue">
          <OrderQueue
            accountId={accountId}
            rows={queue.data ?? []}
            timeZone={zone}
            isLoading={queue.isLoading || timeZone.isLoading}
            canWork={canWork}
          />
        </TabsContent>

        <TabsContent value="all">
          <OrdersTable accountId={accountId} zone={zone} zoneReady={timeZone.isSuccess} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrdersTable({
  accountId,
  zone,
  zoneReady,
}: {
  accountId: string;
  zone: string;
  zoneReady: boolean;
}) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<OrdersFilters>(defaultOrdersFilters);
  const [search, setSearch] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() => ({
    ...DEFAULT_HIDDEN,
    ...readColumnVisibility(),
  }));
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columnVisibility));
    } catch {
      // Private mode or a full quota: the choice just does not persist.
    }
  }, [columnVisibility]);

  /*
   * Debounced, unlike the customers list.
   *
   * That one filters with a PostgREST `or(...)`; this one calls an RPC that
   * joins customers and drivers and counts items per row for an exact total, so
   * a query per keystroke is a real cost on a large order book rather than a
   * theoretical one.
   */
  useEffect(() => {
    const id = setTimeout(() => {
      // Any filter change resets to page 1: staying on page 7 of a narrower
      // result set would show an empty table.
      setFilters((current) =>
        current.q === search.trim() ? current : { ...current, q: search.trim(), page: 1 },
      );
    }, 300);

    return () => clearTimeout(id);
  }, [search]);

  const page = useQuery({
    queryKey: ['orders', accountId, 'table', zone, filters],
    queryFn: () => listOrders(accountId, filters, zone),
    // Nothing can be fetched before the account is known, and running with the
    // fallback zone first would fetch the wrong day for the `today` preset and
    // then refetch a beat later.
    enabled: Boolean(accountId) && zoneReady,
    // Keep the current page on screen while the next one loads, so paging does
    // not flash an empty table.
    placeholderData: keepPreviousData,
  });

  const rows = page.data?.rows ?? [];
  const total = page.data?.total ?? 0;

  const cols = useMemo(() => columns(zone), [zone]);

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns: cols,
    data: rows,
    // The query returns exactly one page, already ordered and filtered, so the
    // table models none of it. `rowCount` is what lets the footer say
    // "101–200 of 8,431" from a hundred rows in hand.
    manualPagination: true,
    rowCount: total,
    // No column is sortable: `search_orders` fixes the order at
    // `sort_date desc, order_number desc`, and a header that looks clickable
    // but changes nothing is worse than one that does not.
    enableSorting: false,
    state: {
      pagination: { pageIndex: filters.page - 1, pageSize: filters.perPage },
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater({ pageIndex: filters.page - 1, pageSize: filters.perPage })
          : updater;

      // `perPage` is part of the query, so asking for 100 a page re-runs
      // `search_orders` with a wider limit rather than slicing rows already in
      // hand -- there are none to slice.
      setFilters((current) => ({ ...current, page: next.pageIndex + 1, perPage: next.pageSize }));
    },
  });

  const isLoading = page.isLoading || !zoneReady;
  const filtered = hasActiveOrdersFilters(filters);

  async function exportCsv() {
    setExporting(true);

    try {
      const result = await exportOrdersCsv(accountId, filters, zone, {
        status: (value) => ORDER_STATUS_META[value].label,
        fulfillment: (value) => FULFILLMENT_META[value].label,
        payment: (value) => PAYMENT_STATUS_META[value].label,
      });

      if (!result.rows) {
        toast.info('No orders match these filters.');
        return;
      }

      const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');

      anchor.href = url;
      anchor.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();

      URL.revokeObjectURL(url);

      if (result.truncated) {
        toast.warning(
          `Exported the first ${EXPORT_MAX_ROWS.toLocaleString()} orders. Narrow the date range to export the rest.`,
        );
      } else {
        toast.success(`Exported ${result.rows.toLocaleString()} orders.`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not export these orders.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {isLoading
            ? 'Loading orders…'
            : `${total.toLocaleString()} order${total === 1 ? '' : 's'}${filtered ? ' matching' : ''}`}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <FacetSelect
            label="All statuses"
            ariaLabel="Filter by status"
            value={filters.statuses[0]}
            options={ORDER_STATUSES.map((value) => ({
              value,
              label: ORDER_STATUS_META[value].label,
            }))}
            onChange={(value) =>
              setFilters((current) => ({ ...current, statuses: value, page: 1 }))
            }
          />

          <FacetSelect
            label="All fulfillment"
            ariaLabel="Filter by fulfillment"
            value={filters.fulfillment[0]}
            options={FULFILLMENT_TYPES.map((value) => ({
              value,
              label: FULFILLMENT_META[value].label,
            }))}
            onChange={(value) =>
              setFilters((current) => ({ ...current, fulfillment: value, page: 1 }))
            }
          />

          <FacetSelect
            label="All payments"
            ariaLabel="Filter by payment status"
            value={filters.payment[0]}
            options={PAYMENT_STATUSES.map((value) => ({
              value,
              label: PAYMENT_STATUS_META[value].label,
            }))}
            onChange={(value) => setFilters((current) => ({ ...current, payment: value, page: 1 }))}
          />

          <Select
            value={filters.range || ALL}
            onValueChange={(value) =>
              setFilters((current) => ({
                ...current,
                range: value === ALL ? '' : (value as OrdersFilters['range']),
                page: 1,
              }))
            }
          >
            <SelectTrigger className="w-36" aria-label="Filter by date">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value={ALL}>Any date</SelectItem>
              {DATE_RANGES.map((value) => (
                <SelectItem key={value} value={value}>
                  {DATE_RANGE_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder="Search order #, name, email or phone…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-64"
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" aria-label="Choose columns">
                <Columns3 className="size-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>Show columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {table
                .getAllLeafColumns()
                .filter((col) => col.getCanHide())
                .map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.id}
                    checked={col.getIsVisible()}
                    onCheckedChange={(checked) => col.toggleVisibility(checked === true)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting || isLoading}>
            <Download className="size-4" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </div>
      </div>

      <DataTable
        table={table}
        columnCount={table.getVisibleLeafColumns().length}
        isLoading={isLoading}
        onRowActivate={(row) => navigate({ to: '/orders/$orderId', params: { orderId: row.id } })}
        emptyMessage={
          filtered
            ? 'No orders match those filters.'
            : 'Orders placed on the storefront or rung up at the register will appear here.'
        }
      />

      <DataTablePagination table={table} total={total} />
    </div>
  );
}

/**
 * One facet of the order book, as a single-select.
 *
 * The RPC takes arrays and ctrl-saas offers multi-select, but the console's
 * established list control is the single `Select` the catalog uses, and one
 * status at a time is what an operator working the board actually picks. The
 * array survives underneath, so widening this to multi-select later is a change
 * to this component and nothing else.
 */
function FacetSelect<T extends string>({
  label,
  ariaLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  ariaLabel: string;
  value: T | undefined;
  options: { value: T; label: string }[];
  onChange: (value: T[]) => void;
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={(next) => onChange(next === ALL ? [] : [next as T])}
    >
      <SelectTrigger className="w-40" aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>

      <SelectContent>
        <SelectItem value={ALL}>{label}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
